/**
 * Vehicle Brands Section Component
 * Container for displaying all vehicle brand records grouped by type:
 * - Title Brands (numeric codes like "11" for Salvage)
 * - Insurance Records (category code "I")
 * - Junk and Salvage Records (category code "J", with entity details)
 * - Individual Records (category code "J", without entity details - PII protected)
 * Each record is displayed as an independent entry (flat list from NMVTIS).
 * Each section is collapsible with expand/collapse functionality.
 *
 * Supports two display modes:
 * - "Detailed" (default): Shows only detected brands with full details
 * - "Checklist": Shows ALL brand codes with checkmarks/warnings (like NMVTIS reference PDF)
 */

import React, { useState, useEffect } from 'react';
import { FileText, Shield, Building2, User, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { BrandChecklistItem } from '../services/vehicleReportService';
import { formatPhone } from '../utils/validation';

export interface VehicleBrand {
  code: string;
  description: string;
  reportingEntity: string;
  date: string | null;
  reportingEntityCategoryCode?: string;
  reportingEntityCategoryText?: string;
  disposition?: string;
  state?: string;
  city?: string;
  phone?: string;
  email?: string;
  intendedForExport?: boolean;
  isIndividualRecord?: boolean;
}

interface VehicleBrandsSectionProps {
  brands: VehicleBrand[];
  brandChecklist?: BrandChecklistItem[];
  isLoadingChecklist?: boolean;
  onRequestChecklist?: () => void;
  className?: string;
  defaultExpanded?: boolean;
}

type ViewMode = 'detailed' | 'checklist';

const VehicleBrandsSection: React.FC<VehicleBrandsSectionProps> = ({
  brands,
  brandChecklist,
  isLoadingChecklist = false,
  onRequestChecklist,
  className = '',
  defaultExpanded = true
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('detailed');
  const [titleBrandsExpanded, setTitleBrandsExpanded] = useState(defaultExpanded);
  const [insuranceExpanded, setInsuranceExpanded] = useState(defaultExpanded);
  const [junkSalvageExpanded, setJunkSalvageExpanded] = useState(defaultExpanded);
  const [individualExpanded, setIndividualExpanded] = useState(defaultExpanded);

  // Fetch checklist data when switching to checklist view
  useEffect(() => {
    if (viewMode === 'checklist' && !brandChecklist && !isLoadingChecklist && onRequestChecklist) {
      onRequestChecklist();
    }
  }, [viewMode, brandChecklist, isLoadingChecklist, onRequestChecklist]);

  // Count detected issues for badge (exclude JSI categories since they have their own sections)
  const detectedCount = brandChecklist?.filter(b => b.isDetected && !b.isJsiCategory).length ?? 0;

  // Toggle button component - text-based with underline for active
  const ViewToggle = () => (
    <div className="flex items-center gap-4">
      <button
        onClick={() => setViewMode('detailed')}
        className={`text-sm transition-all pb-0.5 ${
          viewMode === 'detailed'
            ? 'text-[#0f172a] font-semibold border-b-2 border-[#0f172a]'
            : 'text-[#94a3b8] hover:text-[#64748b]'
        }`}
      >
        Detailed
      </button>
      <button
        onClick={() => setViewMode('checklist')}
        className={`text-sm transition-all pb-0.5 ${
          viewMode === 'checklist'
            ? 'text-[#0f172a] font-semibold border-b-2 border-[#0f172a]'
            : 'text-[#94a3b8] hover:text-[#64748b]'
        }`}
      >
        Checklist
      </button>
    </div>
  );

  // Format date as MM/YYYY
  const formatBrandDate = (dateStr?: string): string | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${year}`;
  };

  // Checklist view - two-column grid with circle icons
  const ChecklistView = () => {
    if (isLoadingChecklist) {
      return (
        <div className="glass-card-strong p-6 rounded-xl">
          <div className="flex items-center justify-center gap-2 py-8">
            <Loader2 className="w-5 h-5 text-[#64748b] animate-spin" />
            <span className="text-sm text-[#64748b]">Loading brand checklist...</span>
          </div>
        </div>
      );
    }

    if (!brandChecklist || brandChecklist.length === 0) {
      return (
        <div className="glass-card-strong p-6 rounded-xl">
          <div className="p-4 bg-[#64748b]/10 border border-[#64748b]/20 rounded-lg text-center">
            <p className="text-sm text-[#64748b]">Brand checklist data not available.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="glass-card-strong rounded-xl overflow-hidden">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {brandChecklist.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                {item.isDetected ? (
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                )}
                <span className={`text-sm ${item.isDetected ? 'font-bold text-[#0f172a]' : 'text-[#0f172a]'}`}>
                  {item.title}
                </span>
                {item.isDetected && item.brandDate && (
                  <span className="ml-auto text-sm text-[#94a3b8] italic">
                    {formatBrandDate(item.brandDate)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Issue count badge component
  const IssueBadge = () => {
    if (viewMode !== 'checklist' || !brandChecklist) return null;
    if (detectedCount === 0) return null;
    return (
      <div className="flex items-center gap-1.5 text-red-600">
        <AlertTriangle className="w-4 h-4" />
        <span className="text-sm font-semibold">
          {detectedCount} {detectedCount === 1 ? 'issue' : 'issues'} found
        </span>
      </div>
    );
  };

  // Show "No Brands Found" message when there are no brands (detailed view)
  // In checklist mode, always show the full checklist
  if (brands.length === 0 && viewMode === 'detailed') {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-base font-bold text-[#0f172a]">Vehicle Brands</h3>
            <IssueBadge />
          </div>
          {onRequestChecklist && <ViewToggle />}
        </div>
        <div className="glass-card-strong p-6 rounded-xl">
          <div className="p-4 bg-[#16a34a]/10 border border-[#16a34a]/30 rounded-lg text-center">
            <p className="text-sm font-medium text-[#16a34a]">No Brands Found</p>
            <p className="text-xs text-[#64748b] mt-1">No title brands, insurance, junk/salvage, or individual records reported for this vehicle.</p>
          </div>
        </div>
      </div>
    );
  }

  if (brands.length === 0 && viewMode === 'checklist') {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-base font-bold text-[#0f172a]">Vehicle Brands</h3>
            <IssueBadge />
          </div>
          {onRequestChecklist && <ViewToggle />}
        </div>
        <ChecklistView />
      </div>
    );
  }

  // Categorize brands by reporting entity type
  const titleBrands = brands.filter(b => {
    const catCode = b.reportingEntityCategoryCode || '';
    return catCode !== 'J' && catCode !== 'I';
  });
  const insuranceRecords = brands.filter(b => b.reportingEntityCategoryCode === 'I');
  const junkSalvageRecords = brands.filter(
    b => b.reportingEntityCategoryCode === 'J' && !b.isIndividualRecord
  );
  const individualRecords = brands.filter(
    b => b.reportingEntityCategoryCode === 'J' && b.isIndividualRecord
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-bold text-[#0f172a]">Vehicle Brands</h3>
          <IssueBadge />
        </div>
        {onRequestChecklist && <ViewToggle />}
      </div>

      {viewMode === 'checklist' ? (
        <ChecklistView />
      ) : (
        <>
          {/* Title Brands Section */}
          {titleBrands.length > 0 && (
            <div className="glass-card-strong rounded-xl overflow-hidden">
              <button
                onClick={() => setTitleBrandsExpanded(!titleBrandsExpanded)}
                className="w-full p-4 flex items-center justify-between hover:bg-black/[0.05] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-yellow-400" />
                  <h3 className="text-sm font-bold text-[#0f172a]">Title Brands ({titleBrands.length})</h3>
                </div>
                {titleBrandsExpanded ? (
                  <ChevronUp className="w-5 h-5 text-[#64748b]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#64748b]" />
                )}
              </button>
              {titleBrandsExpanded && (
                <div className="px-4 pb-4 space-y-3">
                  {titleBrands.map((brand, index) => (
                    <div key={index} className="p-4 bg-white/40 rounded-lg border border-black/[0.08] border-l-2 border-l-yellow-500">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                        <div>
                          <span className="text-[#64748b] block mb-1">Brand</span>
                          <span className="text-[#0f172a] font-medium">{brand.description || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-[#64748b] block mb-1">Code</span>
                          <span className="text-[#0f172a] font-medium font-mono">{brand.code || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-[#64748b] block mb-1">Reported By</span>
                          <span className="text-[#0f172a] font-medium">{brand.reportingEntity || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-[#64748b] block mb-1">Date</span>
                          <span className="text-[#0f172a] font-medium">
                            {brand.date ? new Date(brand.date).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Insurance Records Section - always visible regardless of view mode */}
      {insuranceRecords.length > 0 && (
        <div className="glass-card-strong rounded-xl overflow-hidden">
          <button
            onClick={() => setInsuranceExpanded(!insuranceExpanded)}
            className="w-full p-4 flex items-center justify-between hover:bg-black/[0.05] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-purple-400" />
              <h3 className="text-sm font-bold text-[#0f172a]">Insurance Records ({insuranceRecords.length})</h3>
            </div>
            {insuranceExpanded ? (
              <ChevronUp className="w-5 h-5 text-[#64748b]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#64748b]" />
            )}
          </button>
          {insuranceExpanded && (
            <div className="px-4 pb-4 space-y-3">
              {insuranceRecords.map((record, index) => (
                <div key={index} className="p-4 bg-white/40 rounded-lg border border-black/[0.08] border-l-2 border-l-purple-500">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <span className="text-[#64748b] block mb-1">Insurance Company</span>
                      <span className="text-[#0f172a] font-medium">{record.reportingEntity || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-[#64748b] block mb-1">Disposition</span>
                      <span className="text-[#0f172a] font-medium">{record.disposition || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-[#64748b] block mb-1">Location</span>
                      <span className="text-[#0f172a] font-medium">
                        {record.city && record.state
                          ? `${record.city}, ${record.state}`
                          : record.city || record.state || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#64748b] block mb-1">Obtained Date</span>
                      <span className="text-[#0f172a] font-medium">
                        {record.date ? new Date(record.date).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                  {/* Contact Information */}
                  {(record.email || record.phone) && (
                    <div className="mt-3 pt-3 border-t border-black/[0.08] grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                      {record.email && (
                        <div>
                          <span className="text-[#64748b] block mb-1">Email</span>
                          <a
                            href={`mailto:${record.email}`}
                            className="text-purple-600 hover:text-purple-700 transition-colors"
                          >
                            {record.email}
                          </a>
                        </div>
                      )}
                      {record.phone && (
                        <div>
                          <span className="text-[#64748b] block mb-1">Phone</span>
                          <a
                            href={`tel:${record.phone}`}
                            className="text-purple-600 hover:text-purple-700 transition-colors"
                          >
                            {formatPhone(record.phone)}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* NMVTIS Explanatory Note - Compliance Required */}
      {(insuranceRecords.length > 0 || junkSalvageRecords.length > 0) && (
        <div className="p-4 bg-blue-50 border border-blue-200 border-l-4 border-l-blue-500 rounded-lg">
          <p className="text-[13px] text-[#0f172a] leading-relaxed">
            <strong><em>Explanatory Note:</em></strong>{' '}
            If this VIN has a record in the Junk/Salvage or Insurance information then the business that submitted the VIN to NMVTIS deemed the vehicle to be either a junk, salvage, or in the case of an insurer, a total loss. The information in the <strong>DISPOSITION</strong> field in the Junk/Salvage section denotes what has happened to the VIN (i.e., vehicle) since it came into the possession of the business.
          </p>
        </div>
      )}

      {/* Junk and Salvage Records Section - always visible regardless of view mode */}
      {junkSalvageRecords.length > 0 && (
        <div className="glass-card-strong rounded-xl overflow-hidden">
          <button
            onClick={() => setJunkSalvageExpanded(!junkSalvageExpanded)}
            className="w-full p-4 flex items-center justify-between hover:bg-black/[0.05] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-orange-400" />
              <h3 className="text-sm font-bold text-[#0f172a]">Junk and Salvage Records ({junkSalvageRecords.length})</h3>
            </div>
            {junkSalvageExpanded ? (
              <ChevronUp className="w-5 h-5 text-[#64748b]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#64748b]" />
            )}
          </button>
          {junkSalvageExpanded && (
            <div className="px-4 pb-4 space-y-3">
              {junkSalvageRecords.map((record, index) => (
                <div key={index} className="p-4 bg-white/40 rounded-lg border border-black/[0.08] border-l-2 border-l-orange-500">
                  <div className="flex items-start justify-between">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-xs flex-1">
                      <div>
                        <span className="text-[#64748b] block mb-1">Reporting Entity</span>
                        <span className="text-[#0f172a] font-medium">{record.reportingEntity || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="text-[#64748b] block mb-1">Disposition</span>
                        <span className="text-[#0f172a] font-medium">{record.disposition || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-[#64748b] block mb-1">Location</span>
                        <span className="text-[#0f172a] font-medium">
                          {record.city && record.state
                            ? `${record.city}, ${record.state}`
                            : record.city || record.state || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#64748b] block mb-1">Export</span>
                        <span className={`font-medium ${record.intendedForExport ? 'text-red-600' : 'text-[#0f172a]'}`}>
                          {record.intendedForExport ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#64748b] block mb-1">Obtained Date</span>
                        <span className="text-[#0f172a] font-medium">
                          {record.date ? new Date(record.date).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                    {record.intendedForExport && (
                      <span className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded border border-red-200">
                        Intended for Export
                      </span>
                    )}
                  </div>
                  {/* Contact Information */}
                  {(record.email || record.phone) && (
                    <div className="mt-3 pt-3 border-t border-black/[0.08] grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                      {record.email && (
                        <div>
                          <span className="text-[#64748b] block mb-1">Email</span>
                          <a
                            href={`mailto:${record.email}`}
                            className="text-orange-600 hover:text-orange-700 transition-colors"
                          >
                            {record.email}
                          </a>
                        </div>
                      )}
                      {record.phone && (
                        <div>
                          <span className="text-[#64748b] block mb-1">Phone</span>
                          <a
                            href={`tel:${record.phone}`}
                            className="text-orange-600 hover:text-orange-700 transition-colors"
                          >
                            {formatPhone(record.phone)}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Individual Records Section - always visible regardless of view mode */}
      {individualRecords.length > 0 && (
        <div className="glass-card-strong rounded-xl overflow-hidden">
          <button
            onClick={() => setIndividualExpanded(!individualExpanded)}
            className="w-full p-4 flex items-center justify-between hover:bg-black/[0.05] transition-colors"
          >
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-bold text-[#0f172a]">Individual Records ({individualRecords.length})</h3>
            </div>
            {individualExpanded ? (
              <ChevronUp className="w-5 h-5 text-[#64748b]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#64748b]" />
            )}
          </button>
          {individualExpanded && (
            <div className="px-4 pb-4">
              <p className="text-xs text-[#64748b] mb-3">
                Records submitted by individuals. Contact information is protected per federal privacy requirements.
              </p>
              <div className="space-y-3">
                {individualRecords.map((record, index) => (
                  <div key={index} className="p-4 bg-white/40 rounded-lg border border-black/[0.08] border-l-2 border-l-cyan-500">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <span className="text-[#64748b] block mb-1">Reporter Type</span>
                        <span className="text-[#0f172a] font-medium">Individual</span>
                      </div>
                      <div>
                        <span className="text-[#64748b] block mb-1">Disposition</span>
                        <span className="text-[#0f172a] font-medium">{record.disposition || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-[#64748b] block mb-1">State</span>
                        <span className="text-[#0f172a] font-medium">{record.state || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-[#64748b] block mb-1">Date</span>
                        <span className="text-[#0f172a] font-medium">
                          {record.date ? new Date(record.date).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleBrandsSection;
